import { type CSSProperties, type ReactNode, type RefObject, type WheelEventHandler } from "react";

import { cn } from "@/lib/classNames";
import { getMapChromeTheme } from "@/components/mapChromeTheme";

interface SelectionCarouselProps {
  anchorClassName?: string;
  panelClassName?: string;
  headerLeft?: ReactNode;
  headerRight?: ReactNode;
  topRightOverlay?: ReactNode;
  viewportRef?: RefObject<HTMLDivElement | null>;
  viewportClassName?: string;
  viewportStyle?: CSSProperties;
  trackClassName?: string;
  onWheel?: WheelEventHandler<HTMLDivElement>;
  canScrollLeft?: boolean;
  canScrollRight?: boolean;
  onScrollLeft?: () => void;
  onScrollRight?: () => void;
  previousLabel?: string;
  nextLabel?: string;
  showEdgeFades?: boolean;
  children: ReactNode;
}

function CarouselArrow({ direction }: { direction: "left" | "right" }) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d={direction === "left" ? "m15 18-6-6 6-6" : "m9 18 6-6-6-6"} />
    </svg>
  );
}

export default function SelectionCarousel({
  anchorClassName,
  panelClassName,
  headerLeft,
  headerRight,
  topRightOverlay,
  viewportRef,
  viewportClassName,
  viewportStyle,
  trackClassName,
  onWheel,
  canScrollLeft = false,
  canScrollRight = false,
  onScrollLeft,
  onScrollRight,
  previousLabel = "Show previous items",
  nextLabel = "Show more items",
  showEdgeFades = false,
  children,
}: SelectionCarouselProps) {
  const theme = getMapChromeTheme();
  const hasHeader = headerLeft != null || headerRight != null;
  const showLeftButton = Boolean(canScrollLeft && onScrollLeft);
  const showRightButton = Boolean(canScrollRight && onScrollRight);

  return (
    <div
      className={cn(
        "pointer-events-none fixed left-1/2 z-10 -translate-x-1/2",
        anchorClassName,
      )}
    >
      <div
        className={cn(
          "relative pointer-events-auto p-3",
          theme.floatingPanel,
          panelClassName,
        )}
      >
        {topRightOverlay}
        {hasHeader ? (
          <div className="mb-3 flex items-center justify-between gap-3">
            <div className="min-w-0">{headerLeft}</div>
            {headerRight ? <div className="shrink-0">{headerRight}</div> : null}
          </div>
        ) : null}
        <div className="relative">
          {showLeftButton ? (
            <button
              type="button"
              onClick={onScrollLeft}
              aria-label={previousLabel}
              className={`absolute left-2 top-1/2 z-10 -translate-y-1/2 ${theme.carouselArrow}`}
            >
              <CarouselArrow direction="left" />
            </button>
          ) : null}
          {showRightButton ? (
            <button
              type="button"
              onClick={onScrollRight}
              aria-label={nextLabel}
              className={`absolute right-2 top-1/2 z-10 -translate-y-1/2 ${theme.carouselArrow}`}
            >
              <CarouselArrow direction="right" />
            </button>
          ) : null}
          {showEdgeFades ? (
            <>
              <div
                className={`pointer-events-none absolute inset-y-0 left-0 z-1 w-16 ${theme.carouselEdgeFadeLeft}`}
              />
              <div
                className={`pointer-events-none absolute inset-y-0 right-0 z-1 w-16 ${theme.carouselEdgeFadeRight}`}
              />
            </>
          ) : null}
          <div
            ref={viewportRef}
            onWheel={onWheel}
            className={cn(
              "overflow-x-auto overflow-y-hidden",
              showLeftButton || showRightButton ? "scroll-smooth" : null,
              viewportClassName,
            )}
            style={viewportStyle}
          >
            <div className={cn("flex w-fit gap-2", trackClassName)}>{children}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
